import { useState, useRef, useEffect, useCallback } from 'react';
import { cn } from '@/lib/utils';

interface NumberCellProps {
  value: number | null;
  onChange: (value: number | null) => void;
  isEditing: boolean;
  onStartEdit: () => void;
  onEndEdit: () => void;
  decimalPlaces?: number;
  prefix?: string;
  suffix?: string;
  min?: number;
  max?: number;
  thousandsSeparator?: boolean;
  className?: string;
}

export function NumberCell({
  value,
  onChange,
  isEditing,
  onStartEdit,
  onEndEdit,
  decimalPlaces = 0,
  prefix = '',
  suffix = '',
  min,
  max,
  thousandsSeparator = false,
  className,
}: NumberCellProps) {
  const [editValue, setEditValue] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isEditing) {
      setEditValue(value?.toString() ?? '');
      setTimeout(() => {
        inputRef.current?.focus();
        inputRef.current?.select();
      }, 0);
    }
  }, [isEditing, value]);

  const formatNumber = useCallback(
    (num: number | null): string => {
      if (num === null || num === undefined) return '';

      let formatted = num.toFixed(decimalPlaces);

      if (thousandsSeparator) {
        const parts = formatted.split('.');
        parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
        formatted = parts.join('.');
      }

      return `${prefix}${formatted}${suffix}`;
    },
    [decimalPlaces, thousandsSeparator, prefix, suffix]
  );

  const parseValue = useCallback(
    (str: string): number | null => {
      const cleaned = str.replace(/[^0-9.-]/g, '');
      if (cleaned === '' || cleaned === '-') return null;

      let num = parseFloat(cleaned);
      if (isNaN(num)) return null;

      // Apply constraints
      if (min !== undefined && num < min) num = min;
      if (max !== undefined && num > max) num = max;

      // Round to decimal places
      const factor = Math.pow(10, decimalPlaces);
      num = Math.round(num * factor) / factor;

      return num;
    },
    [min, max, decimalPlaces]
  );

  const handleSave = useCallback(() => {
    const parsed = parseValue(editValue);
    if (parsed !== value) {
      onChange(parsed);
    }
    onEndEdit();
  }, [editValue, value, parseValue, onChange, onEndEdit]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        handleSave();
      } else if (e.key === 'Escape') {
        e.preventDefault();
        setEditValue(value?.toString() ?? '');
        onEndEdit();
      } else if (e.key === 'Tab') {
        handleSave();
      }
    },
    [handleSave, onEndEdit, value]
  );

  if (isEditing) {
    return (
      <input
        ref={inputRef}
        type="text"
        inputMode="decimal"
        value={editValue}
        onChange={(e) => setEditValue(e.target.value)}
        onBlur={handleSave}
        onKeyDown={handleKeyDown}
        className={cn(
          'w-full h-full px-2 py-1 text-sm text-right bg-white border-2 border-blue-500 outline-none',
          'rounded-none focus:ring-0',
          className
        )}
      />
    );
  }

  return (
    <div
      className={cn(
        'w-full h-full px-2 py-1 text-sm text-right truncate cursor-text',
        value === null && 'text-gray-400',
        className
      )}
      onDoubleClick={onStartEdit}
    >
      {value !== null ? formatNumber(value) : ''}
    </div>
  );
}

export default NumberCell;
